import {
  DO_NOT_USE_platformMetrics,
  type IAnalytics,
  type IReports,
} from '@sail/observability';
import {DEFAULT_LOCALE} from 'src/internal/constants';
import createIntlWithLocale from 'src/internal/createIntlWithLocale';
import {handleReactIntlError} from 'src/internal/errors/handleReactIntlError/handleReactIntlError';

import type {FormattedMessage} from 'src/public/formatMessage/types';
import type {
  MessageDescriptor,
  UnionToIntersection,
  messageDescriptorUniqueIdentifierType,
} from 'src/public/message/types';
import type {IntlConfig} from 'src/types';

/**
 * Formats messages previously defined with the `message` function. The
 * translated version of the provided message will be resolved based on the
 * `locale` `@sail/intl` was initialized with.
 *
 * `formatMessage` implements a fallback strategy when formatting fails or a
 * translation is missing; worst-case scenario a non-empty string is returned.
 * Here's the message formatting fallback algorithm in order:
 *
 *   1. Lookup and format the translated message given its id.
 *   2. Fallback to formatting the defaultMessage.
 *   3. Fallback to the literal message id.
 *   4. Fallback to an empty string.
 *
 * Make sure `@sail/intl` is properly setup as explained in the [Installation &
 * setup](/apis/intl/installation-and-setup) guide.
 *
 * See [Localized messages](/apis/intl/localized-messages) for more details.
 *
 * @example Basic {{include "./examples/formatMessage.basic.tsx"}}
 * @see https://sail.stripe.me/apis/intl/useIntl#format-message
 */
export default function formatMessage<
  /** Generic type for the provided message descriptor map object */
  TMessage extends MessageDescriptor<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
  /** Generic type for the message values to be passed-in to the formatter  */
  TValues extends UnionToIntersection<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
>(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The message descriptor map object passed-in for formatting */
  descriptor: TMessage,
  /** The values allowed to be passed-in to the message */
  ...values: TValues extends Record<string, any>
    ? [UnionToIntersection<TValues>]
    : [never?]
): FormattedMessage<TValues> {
  const locale =
    descriptor?.translate === false ? DEFAULT_LOCALE : config.locale;
  try {
    const defaultMessage = descriptor?.defaultMessage;
    DO_NOT_USE_platformMetrics.trackTransalationCount('request');
    DO_NOT_USE_platformMetrics.translationCountForMessageLength(
      'request',
      defaultMessage?.length ?? 0,
    );
    const intl = createIntlWithLocale(locale, config, reports, analytics);

    return intl.formatMessage(
      {
        id: descriptor?.id,
        defaultMessage,
      },
      values[0],
    );
  } catch (error) {
    handleReactIntlError(reports, analytics, locale, error);

    return descriptor?.defaultMessage ?? descriptor?.id ?? '';
  }
}

export type FormatMessage = typeof formatMessage;

export type FormatMessageDeclarative = <
  TMessage extends MessageDescriptor<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
  TValues extends UnionToIntersection<
    TMessage[messageDescriptorUniqueIdentifierType]
  >,
>(
  descriptor: TMessage,
  ...values: TValues extends Record<string, any>
    ? [UnionToIntersection<TValues>]
    : [never?]
) => FormattedMessage<TValues>;
